import { isAxiosError } from 'axios'

import { apiClientVersioned, returnData } from '../index'

import type { IChatSurvey } from '../types/chatSurvey'

const CHAT_SURVEY_CONFIG_PATH = '/config/chat_survey'

interface IChatSurveyConfigRow {
  value: string
}

export const CHAT_SURVEY_DEFAULT_IDLE_SECONDS = 30
export const CHAT_SURVEY_MIN_OPTIONS = 2
export const CHAT_SURVEY_DEFAULT_MIN_REPLIES = 2
export const CHAT_SURVEY_DEFAULT_MAX_DISMISSALS = 3
export const CHAT_SURVEY_DEFAULT_CHAT_COOLDOWN_HOURS = 24

const wholeNumberOr = (value: unknown, fallback: number, minimum: number) =>
  Number.isInteger(value) && (value as number) >= minimum
    ? (value as number)
    : fallback

/** The config row holds any string, so anything malformed reads as "no survey". */
const parseChatSurvey = (value: string): IChatSurvey | null => {
  try {
    const survey = JSON.parse(value)
    const isValid =
      typeof survey?.is_live === 'boolean' &&
      (survey.published_at === null ||
        typeof survey.published_at === 'string') &&
      typeof survey.question === 'string' &&
      Array.isArray(survey.options) &&
      survey.options.length >= CHAT_SURVEY_MIN_OPTIONS &&
      survey.options.every(
        (option: unknown) =>
          typeof (option as IChatSurvey['options'][number])?.id === 'string' &&
          typeof (option as IChatSurvey['options'][number])?.label === 'string',
      )
    if (!isValid) return null
    // Rows saved before these were editable don't have them.
    return {
      ...survey,
      idle_seconds:
        typeof survey.idle_seconds === 'number' && survey.idle_seconds > 0
          ? survey.idle_seconds
          : CHAT_SURVEY_DEFAULT_IDLE_SECONDS,
      is_dismissable: survey.is_dismissable !== false,
      display_frequency:
        survey.display_frequency === 'once_per_chat'
          ? 'once_per_chat'
          : 'once_per_session',
      is_reading_time_added: survey.is_reading_time_added !== false,
      min_replies: wholeNumberOr(
        survey.min_replies,
        CHAT_SURVEY_DEFAULT_MIN_REPLIES,
        1,
      ),
      max_dismissals: wholeNumberOr(
        survey.max_dismissals,
        CHAT_SURVEY_DEFAULT_MAX_DISMISSALS,
        0,
      ),
      chat_cooldown_hours: wholeNumberOr(
        survey.chat_cooldown_hours,
        CHAT_SURVEY_DEFAULT_CHAT_COOLDOWN_HOURS,
        0,
      ),
    }
  } catch {
    return null
  }
}

export const ChatSurveyApi = {
  /** Readable by any signed-in user. `null` until an admin first saves one (404). */
  get: async () => {
    try {
      const { value } = await returnData(
        apiClientVersioned.get<IChatSurveyConfigRow>(CHAT_SURVEY_CONFIG_PATH),
      )
      return parseChatSurvey(value)
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 404) return null
      throw error
    }
  },
  /** Admin only. Replaces the whole row, so the last save wins. */
  save: async (survey: IChatSurvey) => {
    await returnData(
      apiClientVersioned.post<IChatSurveyConfigRow>(CHAT_SURVEY_CONFIG_PATH, {
        value: JSON.stringify(survey),
      }),
    )
    return survey
  },
}
